import React, { Suspense, useEffect, useState, type PropsWithChildren, type FC } from 'react';

import { isBrowser } from '@lucid/utils';
import { useAbTestReducer } from '@lucid/hooks';

import globalConfig from './global';
import chartConfig from './chart';
import sparkConfig from './spark';
import coConfig from './co';
import type { Lucid } from '../../../types/types';
import ABTestAdminUi from '../../../components/internal/ab-test-admin-ui/ab-test-admin-ui';
import ABTestContextGlobal from '../context/ab-test-context-global';
import ABTestShield from '../components/ab-test-shield';

/** Allows to optionally extract Layout props to configure individual test logic */
export type ABTestConfigProps = Partial<Lucid.Context.Page.Layout>;
/** An individual Config type, which is a function that calls the useAbTestInit() hook */
export type ConfigImportType = (props: ABTestConfigProps) => Lucid.Context.ABTest.Props;
/** The config handler to be passed to the ABTestConfig FC */
type ConfigureABTestsType = (props: ABTestConfigProps) => Lucid.Context.ABTest.Props[];

/**
 * * Renders compiled AB Test configurations within the scope of the global context
 * * Updates the global context to reflect all AB Test configurations
 */
const ABTestConfig: FC<{ config: ConfigureABTestsType; layoutContextProps: Lucid.Context.Page.Layout }> = ({
  config,
  layoutContextProps,
}) => {
  config(layoutContextProps);

  return null;
};

/**
 * Provides global AB Testing context and configuration within Layout components.
 *
 * This provider compiles and initializes AB Test configurations based on the current product,
 * manages AB Test state, and conditionally renders the AB Test admin UI and shield components.
 *
 * @remarks
 * - Uses `useAbTestReducer` to manage AB Test states.
 * - Dynamically selects product-specific and global AB Test configs.
 *   - *To configure AB Tests, import them in the respective product config files and add them to the global config.*
 * - Shows admin UI if `showAbTestingUi` is set in localStorage.
 * - Displays and removes AB Test Shield, if shouldShowShield is true for any in-scope tests meeting trigger conditions.
 *
 * @example
 * ```tsx
 * <ABTestGlobalProvider {...layoutContext}>
 *   {children}
 * </ABTestGlobalProvider>
 * ```
 *
 * @see {@link Lucid.Context.Page.Layout}
 * @see {@link Lucid.Context.ABTest.Props}
 */
const ABTestGlobalProvider: FC<PropsWithChildren<Lucid.Context.Page.Layout>> = ({
  children,
  ...layoutContextProps
}) => {
  // Filter and compile AB Test initializer functions for the current product
  const configureAbTests: ConfigureABTestsType = props => {
    const filterConfigs = (configs: ConfigImportType[]): Lucid.Context.ABTest.Props[] =>
      configs.length ? configs.map(c => c(props)) : [];

    let productConfig: ConfigImportType[] = [];

    switch (props.product) {
      case 'chart':
        productConfig = chartConfig;
        break;
      case 'co':
        productConfig = coConfig;
        break;
      case 'spark':
        productConfig = sparkConfig;
    }

    return filterConfigs([...globalConfig, ...productConfig]);
  };

  const [shouldShowABTestButtons, setShouldShowABTestButtons] = useState(false);
  const [abTests, dispatchAbTest] = useAbTestReducer();
  const allTestConfig = configureAbTests(layoutContextProps);
  const shouldShowShieldInitial = allTestConfig.some(c => c.shouldShowShield && c.willTriggerFire);
  const [shouldShowShield, setShouldShowShield] = useState(shouldShowShieldInitial);

  useEffect(() => {
    if (isBrowser()) {
      setShouldShowABTestButtons(window.localStorage.getItem('showAbTestingUi') === '1');
      setShouldShowShield(false);
    }
  }, []);

  const context = {
    tests: abTests,
    dispatchAbTest,
  } as Lucid.Context.ABTest.Global;

  return (
    <ABTestContextGlobal.Provider value={context}>
      {shouldShowShield && <ABTestShield shouldShowShield={shouldShowShield} />}
      <ABTestConfig config={configureAbTests} layoutContextProps={layoutContextProps} />
      {children}
      <Suspense fallback={<div key="abTestsSuspenseFallback" id="abtests-suspense-fallback" />}>
        {shouldShowABTestButtons && !!Object.keys(abTests).length && <ABTestAdminUi abTests={abTests} />}
      </Suspense>
    </ABTestContextGlobal.Provider>
  );
};

export { ABTestGlobalProvider };
export default ABTestGlobalProvider;
